import * as React from "react"
import { createPortal } from "react-dom"

import { cn } from "@/lib/utils"
import { useIsWideScreen } from "./use-mobile"
import {
  pushFullscreenOverlay,
  popFullscreenOverlay,
} from "@/lib/native/flight-map-overlay"
import { useFieldNavigator } from "@/lib/hooks/useFieldNavigator"
import { useKeyboardHeight } from "@/lib/keyboard"

/* ------------------------------------------------------------------ */
/*  Scroll area                                                        */
/* ------------------------------------------------------------------ */

// Non-footer drawer content scrolls here; registering the container with the
// field navigator gives every drawer the iOS keyboard accessory bar
// (Prev/Next/Done) over its text fields. Fields inside DrawerFooter sit
// outside this area and are not navigable — footers only hold buttons.
function DrawerScrollArea({ children }: { children: React.ReactNode }) {
  const { containerRef: scrollRef } = useFieldNavigator()
  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */

type DrawerContextValue = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DrawerContext = React.createContext<DrawerContextValue>({
  open: false,
  onOpenChange: () => {},
})

function useDrawerContext() {
  return React.useContext(DrawerContext)
}

/* ------------------------------------------------------------------ */
/*  Root                                                               */
/* ------------------------------------------------------------------ */

interface DrawerProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  /** kept for API compat – ignored */
  shouldScaleBackground?: boolean
}

function Drawer({ open = false, onOpenChange, children }: DrawerProps) {
  const handleOpenChange = React.useCallback(
    (next: boolean) => onOpenChange?.(next),
    [onOpenChange],
  )

  return (
    <DrawerContext.Provider value={{ open, onOpenChange: handleOpenChange }}>
      {children}
    </DrawerContext.Provider>
  )
}
Drawer.displayName = "Drawer"

/* ------------------------------------------------------------------ */
/*  Content                                                            */
/* ------------------------------------------------------------------ */

const DrawerContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, style, ...props }, ref) => {
  const { open, onOpenChange } = useDrawerContext()
  const isWide = useIsWideScreen()
  const [mounted, setMounted] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  // Keyboard.resize is "none", so the webview never resizes for the keyboard.
  // Lift the sheet above it ourselves — animated, instead of the full-page
  // relayout flash the native resize used to cause.
  const keyboardHeight = useKeyboardHeight()

  React.useEffect(() => {
    if (open) {
      setMounted(true)
      // next frame so the enter animation triggers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })
    } else {
      setVisible(false)
      const id = setTimeout(() => setMounted(false), 300) // match duration
      return () => clearTimeout(id)
    }
  }, [open])

  // The home/flight routes host a fullscreen native MKMapView behind a
  // PassthroughWebView. While this modal is mounted, claim the whole webview as
  // interactive so taps on inputs aren't forwarded to the map underneath.
  React.useEffect(() => {
    if (!mounted) return
    pushFullscreenOverlay()
    return () => {
      popFullscreenOverlay()
    }
  }, [mounted])

  // Separate DrawerFooter children so they stay fixed outside the scroll area
  const footerChildren: React.ReactNode[] = []
  const otherChildren: React.ReactNode[] = []

  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement(child) &&
      (child.type as any)?.displayName === "DrawerFooter"
    ) {
      footerChildren.push(child)
    } else {
      otherChildren.push(child)
    }
  })

  if (!mounted) return null

  return createPortal(
    <>
      {/* overlay */}
      <div
        className={cn(
          "fixed inset-0 z-60 bg-black/30 transition-opacity duration-300 ease-out",
          visible ? "opacity-100" : "opacity-0",
        )}
        onClick={() => onOpenChange(false)}
      />
      {isWide ? (
        /* iPad / large screens: centered dialog. The wrapper is click-through
           (pointer-events-none) so clicks outside the panel reach the overlay
           and close it. */
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4 pointer-events-none"
          style={keyboardHeight > 0 ? { paddingBottom: keyboardHeight } : undefined}
        >
          <div
            ref={ref}
            role="dialog"
            className={cn(
              "pointer-events-auto flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border bg-surface-L0 shadow-xl transition-all duration-200 ease-out lg:min-w-[860px]",
              visible ? "opacity-100 scale-100" : "opacity-0 scale-95",
              className,
            )}
            style={style}
            {...props}
            // The portal escapes the DOM tree but not the React tree: without
            // this, gestures inside the drawer bubble into React-ancestor
            // pointer handlers (ResizableDrawer resize, swipeable rows).
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onPointerCancel={(e) => e.stopPropagation()}
          >
            <DrawerScrollArea>{otherChildren}</DrawerScrollArea>
            {footerChildren}
          </div>
        </div>
      ) : (
        /* Phone: bottom sheet sliding up from the bottom edge. */
        <div
          ref={ref}
          role="dialog"
          className={cn(
            "app-frame drawer-max-h fixed inset-x-0 bottom-0 z-60 flex h-auto flex-col rounded-t-[20px] border bg-surface-L0 overflow-hidden pb-[var(--safe-bottom)] transition-[translate,bottom,max-height,padding-bottom] duration-300 ease-out",
            visible ? "translate-y-0" : "translate-y-full",
            className,
          )}
          style={{
            // While the keyboard is up, sit on top of it (the safe-area pad is
            // redundant there — the keyboard covers the home indicator) and cap
            // the height to the visible strip so the footer stays reachable.
            ...(keyboardHeight > 0
              ? {
                  bottom: keyboardHeight,
                  maxHeight: `calc(100dvh - ${keyboardHeight}px)`,
                  paddingBottom: 0,
                }
              : undefined),
            ...style,
          }}
          {...props}
          // See wide branch: keep pointer gestures from crossing the portal
          // boundary into React-ancestor handlers.
          onPointerDown={(e) => e.stopPropagation()}
          onPointerMove={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onPointerCancel={(e) => e.stopPropagation()}
        >
          <div className="mx-auto mt-4 h-1.5 w-[100px] shrink-0 rounded-full bg-surface-L2" />
          <DrawerScrollArea>{otherChildren}</DrawerScrollArea>
          {footerChildren}
        </div>
      )}
    </>,
    document.body,
  )
})
DrawerContent.displayName = "DrawerContent"

/* ------------------------------------------------------------------ */
/*  Close                                                              */
/* ------------------------------------------------------------------ */

const DrawerClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { asChild?: boolean }
>(({ onClick, asChild, children, ...props }, ref) => {
  const { onOpenChange } = useDrawerContext()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onOpenChange(false)
    onClick?.(e)
  }

  // asChild: clone the onClick onto the single child element
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        onOpenChange(false)
        ;(children as React.ReactElement<any>).props.onClick?.(e)
      },
    })
  }

  return (
    <button ref={ref} type="button" onClick={handleClick} {...props}>
      {children}
    </button>
  )
})
DrawerClose.displayName = "DrawerClose"

/* ------------------------------------------------------------------ */
/*  Header / Footer / Title / Description (simple layout wrappers)     */
/* ------------------------------------------------------------------ */

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "sticky top-0 z-10 grid gap-1.5 bg-surface-L0 p-4 text-left",
      className,
    )}
    {...props}
  />
)
DrawerHeader.displayName = "DrawerHeader"

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4 shrink-0", className)}
    {...props}
  />
)
DrawerFooter.displayName = "DrawerFooter"

const DrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
))
DrawerTitle.displayName = "DrawerTitle"

const DrawerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-content-primary-subtle", className)}
    {...props}
  />
))
DrawerDescription.displayName = "DrawerDescription"

/* ------------------------------------------------------------------ */
/*  Stubs kept for import compatibility (no-ops)                       */
/* ------------------------------------------------------------------ */

const DrawerTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => <button ref={ref} type="button" {...props} />)
DrawerTrigger.displayName = "DrawerTrigger"

const DrawerPortal = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
)
DrawerPortal.displayName = "DrawerPortal"

const DrawerOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => <div ref={ref} {...props} />)
DrawerOverlay.displayName = "DrawerOverlay"

/* ------------------------------------------------------------------ */
/*  Exports                                                            */
/* ------------------------------------------------------------------ */

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
